<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="show"
        class="fixed inset-0 z-[1200] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
        @click.self="handleClose"
      >
        <div
          class="scraped-lead-modal-sheet relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-lg flex flex-col min-h-0"
          @click.stop
        >
          <!-- Header -->
          <div class="flex items-start justify-between gap-2 px-4 py-2.5 sm:px-6 sm:pt-6 sm:pb-4 border-b border-gray-100 flex-shrink-0">
            <div class="min-w-0 flex-1">
              <h2 class="text-base sm:text-lg font-semibold text-gray-900 leading-snug line-clamp-2">
                <template v-if="mode === 'exit'">{{ $t('colivingSearch.exitIntent.title') }}</template>
                <template v-else-if="mode === 'listing'">{{ colivingTitle || $t('scrapedLeadModal.titleListing', { title: '' }) }}</template>
                <template v-else>{{ $t('scrapedLeadModal.titleSearch', { city }) }}</template>
              </h2>
              <p v-if="mode === 'exit'" class="text-xs sm:text-sm text-gray-500 mt-0.5 line-clamp-2">
                {{ $t('colivingSearch.exitIntent.subtitle', { city: displayCity || $t('colivingSearch.exitIntent.yourCity') }) }}
              </p>
              <p v-else-if="mode === 'search'" class="text-xs sm:text-sm text-gray-500 mt-0.5">
                {{ $t('scrapedLeadModal.subtitleSearch') }}
              </p>
            </div>
            <button
              type="button"
              class="flex-shrink-0 p-1.5 -mr-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              @click="handleClose"
            >
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Success: recomendaciones Spacest tras captura de email -->
          <div
            v-if="submitted && showSpacestSuccess"
            class="scraped-lead-modal-scroll scrollbar-hide flex-1 min-h-0 px-4 py-3 sm:px-6 sm:py-6"
          >
            <div class="text-center mb-3 sm:mb-4">
              <div class="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-2 sm:mb-3">
                <svg class="h-7 w-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-gray-900">
                {{ $t('colivingSearch.leadCaptureSpacest.successTitle') }}
              </h3>
              <p class="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
                {{ $t('colivingSearch.leadCaptureSpacest.successSubtitle', { city: displayCity || $t('colivingSearch.exitIntent.yourCity') }) }}
              </p>
            </div>

            <div class="rounded-xl border border-emerald-200 bg-emerald-50/80 px-3 py-3 mb-4 text-center">
              <p class="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                {{ spacestPromo?.modal?.badge }}
              </p>
              <p class="text-sm text-gray-600 mt-1">{{ spacestPromo?.modal?.useCodeText }}</p>
              <div class="flex items-center justify-center gap-2 mt-2">
                <p class="font-mono text-xl font-bold text-emerald-800 tracking-wide">{{ spacestPromo?.code }}</p>
                <button
                  type="button"
                  class="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                  :class="codeCopied
                    ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                    : 'bg-emerald-600 text-white hover:bg-emerald-700'"
                  @click="copySpacestCode"
                >
                  {{ codeCopied ? (spacestPromo?.modal?.copiedLabel || '✓') : (spacestPromo?.modal?.copyLabel || 'Copiar') }}
                </button>
              </div>
            </div>

            <ul class="space-y-3 mb-4">
              <li
                v-for="listing in spacestPicks"
                :key="listing.id"
                class="flex flex-col gap-2 rounded-xl border border-gray-200 p-2 sm:p-2.5 text-left hover:border-emerald-300 transition-colors"
              >
                <div class="flex gap-2.5 min-w-0">
                  <div class="w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden">
                    <img
                      v-if="cardImage(listing)"
                      :src="cardImage(listing)"
                      :alt="listingTitle(listing)"
                      class="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div class="min-w-0 flex-1 flex flex-col justify-center">
                    <p class="text-sm font-semibold text-gray-900 line-clamp-2">{{ listingTitle(listing) }}</p>
                    <p class="text-xs text-gray-500 mt-0.5 line-clamp-1">{{ listing.propertyLocation || listing.city }}</p>
                    <p v-if="cardPrice(listing)" class="text-xs font-medium text-emerald-700 mt-0.5">
                      {{ $t('colivingSearch.leadCaptureSpacest.fromPrice', { price: cardPrice(listing) }) }}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  class="w-full sm:w-auto sm:self-end flex-shrink-0 px-3 py-2 rounded-full text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700"
                  @click="openSpacestListing(listing)"
                >
                  {{ $t('colivingSearch.leadCaptureSpacest.viewOnSpacest') }}
                </button>
              </li>
            </ul>

            <router-link
              :to="spacestShowcasePath"
              class="mb-3 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-emerald-600 px-4 py-3 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors"
              @click="handleClose"
            >
              {{ $t('colivingSearch.leadCaptureSpacest.browseAllRooms') }}
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </router-link>

            <button
              type="button"
              class="w-full py-2.5 text-sm text-gray-500 hover:text-gray-700"
              @click="handleClose"
            >
              {{ $t('scrapedLeadModal.close') }}
            </button>
          </div>

          <!-- Success genérico (sin Spacest en resultados) -->
          <div
            v-else-if="submitted"
            class="flex-1 flex flex-col items-center justify-center text-center px-1 sm:px-6 py-8 sm:py-10"
          >
            <div class="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <svg class="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-gray-900 mb-2">
              {{ mode === 'exit' ? $t('colivingSearch.exitIntent.successTitle') : $t('scrapedLeadModal.successTitle') }}
            </h3>
            <p class="text-sm text-gray-500 max-w-xs">
              {{ mode === 'exit' ? $t('colivingSearch.exitIntent.successBody') : $t('scrapedLeadModal.successBody') }}
            </p>
            <button
              type="button"
              class="mt-6 px-6 py-2.5 bg-indigo-600 text-white rounded-full text-sm font-medium hover:bg-indigo-700 transition-colors"
              @click="handleClose"
            >
              {{ $t('scrapedLeadModal.close') }}
            </button>
          </div>

          <!-- ═══════ EXIT INTENT: formulario rápido email + consent ═══════ -->
          <form
            v-else-if="mode === 'exit'"
            class="flex flex-col flex-1 min-h-0"
            @submit.prevent="handleSubmit"
          >
            <div class="scraped-lead-modal-scroll scrollbar-hide px-4 py-2.5 sm:px-6 sm:py-4 space-y-3">
              <div>
                <label class="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5">{{ $t('scrapedLeadModal.labelEmail') }}</label>
                <input
                  v-model="form.applicantEmail"
                  type="email"
                  required
                  autocomplete="email"
                  :placeholder="$t('scrapedLeadModal.placeholderEmail')"
                  class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <label class="flex items-start gap-2 cursor-pointer">
                <input
                  v-model="form.consentLeadSharing"
                  type="checkbox"
                  required
                  class="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0"
                />
                <span class="text-[11px] sm:text-xs text-gray-500 leading-snug">
                  {{ $t('scrapedLeadModal.gdprText') }}
                  <router-link to="/privacy-policy" target="_blank" class="text-indigo-500 hover:underline">
                    {{ $t('scrapedLeadModal.gdprLink') }}
                  </router-link>.
                </span>
              </label>

              <p v-if="errorMsg" class="text-xs sm:text-sm text-red-600">{{ errorMsg }}</p>
            </div>

            <div class="scraped-lead-modal-footer">
              <button
                type="submit"
                :disabled="isSubmitting || !form.consentLeadSharing"
                class="w-full py-2.5 sm:py-3 bg-indigo-600 text-white rounded-xl sm:rounded-full text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {{ isSubmitting ? $t('scrapedLeadModal.sending') : $t('colivingSearch.exitIntent.submit') }}
              </button>
            </div>
          </form>

          <!-- ═══════ VARIANT B: Google + reordenado + acordeón ═══════ -->
          <form v-else-if="variant === 'B'" class="flex flex-col flex-1 min-h-0" @submit.prevent="handleSubmit">
            <div class="scraped-lead-modal-scroll scrollbar-hide px-4 py-2.5 sm:px-6 sm:py-4">
            <!-- Google sign-in rápido -->
            <div v-if="!isAuthenticated" class="mb-4">
              <button
                type="button"
                :disabled="isGoogleLoading || isSubmitting"
                class="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                @click="handleGoogleSignIn"
              >
                <svg class="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>{{ $t('scrapedLeadModal.continueWithGoogle') }}</span>
              </button>
              <div class="flex items-center gap-3 my-3">
                <div class="flex-1 h-px bg-gray-100" />
                <span class="text-xs text-gray-400">{{ $t('scrapedLeadModal.orContinueWith') }}</span>
                <div class="flex-1 h-px bg-gray-100" />
              </div>
            </div>

            <!-- Usuario autenticado -->
            <div v-else class="mb-4 flex items-center gap-2 rounded-lg bg-green-50 border border-green-100 px-3 py-2">
              <svg class="h-4 w-4 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <p class="text-xs text-green-800 truncate">{{ $t('scrapedLeadModal.loggedInAs', { email: authEmail }) }}</p>
            </div>

            <!-- Contacto (prioritario) -->
            <fieldset class="mb-4">
              <legend class="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">{{ $t('scrapedLeadModal.sectionPersonal') }}</legend>
              <p class="text-xs text-gray-400 mb-3">{{ $t('scrapedLeadModal.contactHint') }}</p>
              <div class="space-y-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('scrapedLeadModal.labelEmail') }}</label>
                  <input
                    v-model="form.applicantEmail"
                    type="email"
                    autocomplete="email"
                    required
                    :readonly="isAuthenticated && !!authEmail"
                    :placeholder="$t('scrapedLeadModal.placeholderEmail')"
                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('scrapedLeadModal.labelPhone') }}</label>
                  <input
                    v-model="form.applicantPhone"
                    type="tel"
                    autocomplete="tel"
                    placeholder="+34 600 000 000"
                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('scrapedLeadModal.labelName') }}</label>
                  <input
                    v-model="form.applicantName"
                    type="text"
                    autocomplete="name"
                    required
                    :placeholder="$t('scrapedLeadModal.placeholderName')"
                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>
            </fieldset>

            <!-- Datos de estancia (acordeón) -->
            <div class="mb-4 rounded-xl border border-gray-200 overflow-hidden">
              <button
                type="button"
                class="w-full flex items-center justify-between px-3 py-3 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
                @click="showStayDetails = !showStayDetails"
              >
                <div>
                  <p class="text-sm font-medium text-gray-900">{{ $t('scrapedLeadModal.sectionStay') }}</p>
                  <p class="text-xs text-gray-500 mt-0.5">{{ $t('scrapedLeadModal.sectionStayHint') }}</p>
                </div>
                <svg
                  class="h-5 w-5 text-gray-400 flex-shrink-0 ml-2 transition-transform duration-200"
                  :class="{ 'rotate-180': showStayDetails }"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <div v-show="showStayDetails" class="px-3 pb-3 pt-1 space-y-3 border-t border-gray-100">
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('scrapedLeadModal.labelStartDate') }}</label>
                    <input v-model="form.desiredStartDate" type="date" :min="todayDate" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('scrapedLeadModal.labelEndDate') }}</label>
                    <input v-model="form.desiredEndDate" type="date" :min="form.desiredStartDate || todayDate" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                  </div>
                </div>
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('scrapedLeadModal.labelDuration') }}</label>
                    <select v-model.number="form.stayDurationMonths" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
                      <option value="" disabled>{{ $t('scrapedLeadModal.selectDuration') }}</option>
                      <option v-for="m in 11" :key="m" :value="m">{{ m }} {{ m === 1 ? $t('scrapedLeadModal.month') : $t('scrapedLeadModal.months') }}</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('scrapedLeadModal.labelBudget') }}</label>
                    <input v-model.number="form.budgetMax" type="number" min="100" max="10000" step="50" placeholder="900" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                  </div>
                </div>
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('scrapedLeadModal.labelOccupants') }}</label>
                    <select v-model.number="form.occupants" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
                      <option :value="1">{{ $t('scrapedLeadModal.person') }}</option>
                      <option :value="2">{{ $t('scrapedLeadModal.persons') }}</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('scrapedLeadModal.labelReason') }}</label>
                    <select v-model="form.stayReason" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
                      <option value="">{{ $t('scrapedLeadModal.reasonNone') }}</option>
                      <option value="work">{{ $t('scrapedLeadModal.reasonWork') }}</option>
                      <option value="study">{{ $t('scrapedLeadModal.reasonStudy') }}</option>
                      <option value="relocation">{{ $t('scrapedLeadModal.reasonRelocation') }}</option>
                      <option value="travel">{{ $t('scrapedLeadModal.reasonTravel') }}</option>
                      <option value="other">{{ $t('scrapedLeadModal.reasonOther') }}</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('scrapedLeadModal.labelComments') }}</label>
                  <textarea v-model="form.comments" rows="2" :placeholder="$t('scrapedLeadModal.placeholderComments')" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none" />
                </div>
              </div>
            </div>

            <!-- GDPR -->
            <div class="mb-2 sm:mb-4">
              <label class="flex items-start gap-2 cursor-pointer">
                <input v-model="form.consentLeadSharing" type="checkbox" required class="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0" />
                <span class="text-[11px] sm:text-xs text-gray-500 leading-snug">
                  {{ $t('scrapedLeadModal.gdprText') }} <router-link to="/privacy-policy" target="_blank" class="text-indigo-500 hover:underline">{{ $t('scrapedLeadModal.gdprLink') }}</router-link>.
                </span>
              </label>
            </div>

            <!-- Error -->
            <div v-if="errorMsg" class="mb-2 rounded-lg bg-red-50 border border-red-200 px-2.5 py-2">
              <p class="text-xs sm:text-sm text-red-700">{{ errorMsg }}</p>
            </div>

            <!-- Escasez: operadores activos en la ciudad -->
            <div
              v-if="operatorsCount >= 2"
              class="mb-2 flex items-start gap-2 rounded-lg bg-orange-50 border border-orange-100 px-2.5 py-2"
            >
              <p class="text-[11px] sm:text-xs text-orange-800 leading-snug">
                {{ $t('scrapedLeadModal.scarcityMany', { count: operatorsCount, city: displayCity }) }}
              </p>
            </div>
            </div>

            <div class="scraped-lead-modal-footer">
              <button
                type="submit"
                :disabled="isSubmitting || !form.consentLeadSharing"
                class="w-full py-2.5 sm:py-3 bg-indigo-600 text-white rounded-xl sm:rounded-full text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                <svg v-if="isSubmitting" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>{{ isSubmitting ? $t('scrapedLeadModal.sending') : $t('scrapedLeadModal.submit') }}</span>
              </button>
            </div>
          </form>

          <!-- ═══════ VARIANT A: Formulario clásico completo ═══════ -->
          <form v-else class="flex flex-col flex-1 min-h-0" @submit.prevent="handleSubmit">
            <div class="scraped-lead-modal-scroll scrollbar-hide px-4 py-2.5 sm:px-6 sm:py-4">
            <fieldset class="mb-3 sm:mb-5">
              <legend class="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">{{ $t('scrapedLeadModal.sectionPersonal') }}</legend>
              <div class="space-y-2 sm:space-y-3">
                <div>
                  <label class="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5">{{ $t('scrapedLeadModal.labelName') }}</label>
                  <input v-model="form.applicantName" type="text" autocomplete="name" required :placeholder="$t('scrapedLeadModal.placeholderName')" class="w-full rounded-lg border border-gray-300 px-2.5 py-1.5 sm:px-3 sm:py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  <div>
                    <label class="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5">{{ $t('scrapedLeadModal.labelEmail') }}</label>
                    <input v-model="form.applicantEmail" type="email" autocomplete="email" required :placeholder="$t('scrapedLeadModal.placeholderEmail')" class="w-full rounded-lg border border-gray-300 px-2.5 py-1.5 sm:px-3 sm:py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label class="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5">{{ $t('scrapedLeadModal.labelPhone') }}</label>
                    <input v-model="form.applicantPhone" type="tel" autocomplete="tel" placeholder="+34 600 000 000" class="w-full rounded-lg border border-gray-300 px-2.5 py-1.5 sm:px-3 sm:py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                  </div>
                </div>
              </div>
            </fieldset>

            <fieldset class="mb-3 sm:mb-5">
              <legend class="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">{{ $t('scrapedLeadModal.sectionStay') }}</legend>
              <div class="space-y-2 sm:space-y-3">
                <div class="grid grid-cols-2 gap-2 sm:gap-3">
                  <div class="min-w-0">
                    <label class="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5">{{ $t('scrapedLeadModal.labelStartDate') }}</label>
                    <input v-model="form.desiredStartDate" type="date" required :min="todayDate" class="w-full rounded-lg border border-gray-300 px-2 py-1.5 sm:px-3 sm:py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                  </div>
                  <div class="min-w-0">
                    <label class="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5">{{ $t('scrapedLeadModal.labelEndDate') }}</label>
                    <input v-model="form.desiredEndDate" type="date" :min="form.desiredStartDate || todayDate" class="w-full rounded-lg border border-gray-300 px-2 py-1.5 sm:px-3 sm:py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                  </div>
                </div>
                <div class="grid grid-cols-2 gap-2 sm:gap-3">
                  <div class="min-w-0">
                    <label class="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5">{{ $t('scrapedLeadModal.labelDuration') }}</label>
                    <select v-model.number="form.stayDurationMonths" required class="w-full rounded-lg border border-gray-300 px-2 py-1.5 sm:px-3 sm:py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
                      <option value="" disabled>{{ $t('scrapedLeadModal.selectDuration') }}</option>
                      <option v-for="m in 11" :key="m" :value="m">{{ m }} {{ m === 1 ? $t('scrapedLeadModal.month') : $t('scrapedLeadModal.months') }}</option>
                    </select>
                  </div>
                  <div class="min-w-0">
                    <label class="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5">{{ $t('scrapedLeadModal.labelBudget') }}</label>
                    <input v-model.number="form.budgetMax" type="number" min="100" max="10000" step="50" placeholder="900" class="w-full rounded-lg border border-gray-300 px-2 py-1.5 sm:px-3 sm:py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                  </div>
                </div>
                <div class="grid grid-cols-2 gap-2 sm:gap-3">
                  <div class="min-w-0">
                    <label class="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5">{{ $t('scrapedLeadModal.labelOccupants') }}</label>
                    <select v-model.number="form.occupants" class="w-full rounded-lg border border-gray-300 px-2 py-1.5 sm:px-3 sm:py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
                      <option :value="1">{{ $t('scrapedLeadModal.person') }}</option>
                      <option :value="2">{{ $t('scrapedLeadModal.persons') }}</option>
                    </select>
                  </div>
                  <div class="min-w-0">
                    <label class="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5">{{ $t('scrapedLeadModal.labelReason') }}</label>
                    <select v-model="form.stayReason" class="w-full rounded-lg border border-gray-300 px-2 py-1.5 sm:px-3 sm:py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
                      <option value="">{{ $t('scrapedLeadModal.reasonNone') }}</option>
                      <option value="work">{{ $t('scrapedLeadModal.reasonWork') }}</option>
                      <option value="study">{{ $t('scrapedLeadModal.reasonStudy') }}</option>
                      <option value="relocation">{{ $t('scrapedLeadModal.reasonRelocation') }}</option>
                      <option value="travel">{{ $t('scrapedLeadModal.reasonTravel') }}</option>
                      <option value="other">{{ $t('scrapedLeadModal.reasonOther') }}</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label class="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5">{{ $t('scrapedLeadModal.labelComments') }}</label>
                  <textarea v-model="form.comments" rows="2" :placeholder="$t('scrapedLeadModal.placeholderComments')" class="w-full rounded-lg border border-gray-300 px-2.5 py-1.5 sm:px-3 sm:py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none min-h-[3.25rem]" />
                </div>
              </div>
            </fieldset>

            <!-- GDPR -->
            <div class="mb-2 sm:mb-4">
              <label class="flex items-start gap-2 cursor-pointer">
                <input v-model="form.consentLeadSharing" type="checkbox" required class="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0" />
                <span class="text-[11px] sm:text-xs text-gray-500 leading-snug">
                  {{ $t('scrapedLeadModal.gdprText') }} <router-link to="/privacy-policy" target="_blank" class="text-indigo-500 hover:underline">{{ $t('scrapedLeadModal.gdprLink') }}</router-link>.
                </span>
              </label>
            </div>

            <!-- Error -->
            <div v-if="errorMsg" class="mb-2 rounded-lg bg-red-50 border border-red-200 px-2.5 py-2">
              <p class="text-xs sm:text-sm text-red-700">{{ errorMsg }}</p>
            </div>

            <!-- Escasez: operadores activos en la ciudad -->
            <div
              v-if="operatorsCount >= 2"
              class="mb-2 flex items-start gap-2 px-2.5 py-2 rounded-lg bg-orange-50 border border-orange-100"
            >
              <p class="text-[11px] sm:text-xs text-orange-800 leading-snug">
                {{ $t('scrapedLeadModal.scarcityMany', { count: operatorsCount, city: displayCity }) }}
              </p>
            </div>
            </div>

            <div class="scraped-lead-modal-footer">
              <button
                type="submit"
                :disabled="isSubmitting || !form.consentLeadSharing"
                class="w-full py-2.5 sm:py-3 bg-indigo-600 text-white rounded-xl sm:rounded-full text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                <svg v-if="isSubmitting" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>{{ isSubmitting ? $t('scrapedLeadModal.sending') : $t('scrapedLeadModal.submit') }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { getAuth, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useI18n } from 'vue-i18n';
import { useABTest } from '../composables/useABTest';
import { useAnalytics } from '@/composables/useAnalytics';
import {
  getLeadSessionProfile,
  profileToFormDefaults,
  submitSearchLead,
  submitListingLead,
  submitExitLead,
} from '../services/scrapedLeadSubmit';
import { getSpacestPromoCopy } from '../utils/spacestAffiliate';
import { spacestShowcasePath } from '../utils/affiliatePartnerModal';
import {
  pickSpacestRecommendations,
  getListingCardImage,
  getListingCardPrice,
} from '../utils/spacestLeadRecommendations';
import { openExternalColivingListing, resolveExternalApplyUrl } from '../utils/externalRedirect';
import { postOutboundAffiliateNotify } from '../utils/affiliateOutboundNotify';
import { resolveSpacestListingMonthlyPriceEur } from '../utils/spacestListingPrice';

const { t } = useI18n();
const { trackLeadModalOpen, trackScrapedLeadSubmitted } = useAnalytics();

const AB_EXPERIMENT = 'lead_modal_v2';
const variant = useABTest(AB_EXPERIMENT);

const props = defineProps({
  show: { type: Boolean, required: true },
  mode: { type: String, default: 'search' },
  city: { type: String, default: '' },
  coliving: { type: Object, default: null },
  searchFilters: { type: Object, default: null },
  operatorsCount: { type: Number, default: 0 },
  searchResults: { type: Array, default: () => [] },
  userId: { type: String, default: '' },
});

const emit = defineEmits(['close', 'submitted']);

const submitted = ref(false);
const isSubmitting = ref(false);
const isGoogleLoading = ref(false);
const errorMsg = ref('');
const showStayDetails = ref(false);
const isAuthenticated = ref(false);
const authEmail = ref('');

const todayDate = new Date().toISOString().split('T')[0];

const defaultForm = () => ({
  applicantName: '',
  applicantEmail: '',
  applicantPhone: '',
  desiredStartDate: '',
  desiredEndDate: '',
  stayDurationMonths: '',
  budgetMax: null,
  occupants: 1,
  stayReason: '',
  comments: '',
  consentLeadSharing: false,
});

const form = ref(defaultForm());

const colivingTitle = computed(() => {
  if (!props.coliving) return '';
  return props.coliving.title || props.coliving.propertyTitle || props.coliving.externalSource || '';
});

const displayCity = computed(() => {
  return props.city || props.coliving?.propertyLocation || props.coliving?.city || '';
});

const spacestPromo = computed(() => getSpacestPromoCopy());

const spacestPicks = computed(() => {
  if (props.mode !== 'search' && props.mode !== 'exit') return [];
  return pickSpacestRecommendations(props.searchResults, {
    city: displayCity.value,
    limit: 3,
  });
});

const showSpacestSuccess = computed(
  () => (props.mode === 'search' || props.mode === 'exit') && spacestPicks.value.length > 0,
);

function leadCaptureAffiliateSource() {
  return props.mode === 'exit' ? 'lead-capture-exit' : 'lead-capture-search';
}

function listingTitle(listing) {
  return listing?.title || listing?.propertyTitle || 'Spacest';
}

function cardImage(listing) {
  return getListingCardImage(listing);
}

function cardPrice(listing) {
  return getListingCardPrice(listing);
}

const codeCopied = ref(false);
let promoNotifiedThisModal = false;

function notifySpacestPromoCopy(listingForContext = null) {
  if (promoNotifiedThisModal) return;
  const code = spacestPromo.value?.code;
  const referrerEmail = form.value.applicantEmail?.trim();
  if (!code || !referrerEmail) return;
  promoNotifiedThisModal = true;
  const ctx = listingForContext || props.coliving;
  postOutboundAffiliateNotify({
    url: ctx ? resolveExternalApplyUrl(ctx) || '' : '',
    provider: 'spacest',
    colivingId: ctx?.id || props.coliving?.id || '',
    colivingTitle: ctx?.title || ctx?.propertyTitle || props.coliving?.title || '',
    userId: props.userId || '',
    city: displayCity.value || ctx?.propertyLocation || '',
    source: leadCaptureAffiliateSource(),
    action: 'copy-promo-code',
    promoCode: code,
    referrerEmail,
    priceFrom: resolveSpacestListingMonthlyPriceEur(ctx || props.coliving) ?? '',
  });
}

async function copySpacestCode() {
  const code = spacestPromo.value?.code;
  if (!code) return;
  try {
    await navigator.clipboard.writeText(code);
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = code;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }
  codeCopied.value = true;
  notifySpacestPromoCopy();
  setTimeout(() => { codeCopied.value = false; }, 2000);
}

function openSpacestListing(listing) {
  const referrerEmail = form.value.applicantEmail?.trim().toLowerCase() || '';
  emit('close');
  openExternalColivingListing(listing, {
    userId: props.userId || undefined,
    source: leadCaptureAffiliateSource(),
    referrerEmail: referrerEmail || undefined,
  });
}

function syncAuthState() {
  const user = getAuth().currentUser;
  isAuthenticated.value = !!user;
  authEmail.value = user?.email || '';
  if (user?.email && !form.value.applicantEmail) {
    form.value.applicantEmail = user.email;
  }
  if (user?.displayName && !form.value.applicantName) {
    form.value.applicantName = user.displayName;
  }
}

async function handleGoogleSignIn() {
  if (isGoogleLoading.value || isSubmitting.value) return;
  errorMsg.value = '';
  isGoogleLoading.value = true;
  try {
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    const userDocRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);
    if (!userDoc.exists()) {
      await setDoc(userDocRef, {
        email: user.email,
        fullName: user.displayName || '',
        userType: 'applicant',
        profileComplete: false,
        createdAt: new Date(),
        authProvider: 'google',
        emailVerified: true,
        emailVerifiedAt: new Date(),
      });
      try {
        await fetch('/api/email/complete-google-registration', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: user.uid,
            userEmail: user.email,
            userName: user.displayName || user.email?.split('@')[0] || 'Usuario',
          }),
        });
      } catch { /* non-blocking */ }
    }

    form.value.applicantName = user.displayName || form.value.applicantName;
    form.value.applicantEmail = user.email || form.value.applicantEmail;
    syncAuthState();
    await tryPrefillProfile();
  } catch (error) {
    if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
      errorMsg.value = t('scrapedLeadModal.errorGooglePopup');
    } else if (error.code === 'auth/popup-blocked') {
      errorMsg.value = t('scrapedLeadModal.errorGoogleBlocked');
    } else {
      errorMsg.value = t('scrapedLeadModal.errorGoogle');
    }
  } finally {
    isGoogleLoading.value = false;
  }
}

async function tryPrefillProfile() {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return;
  try {
    const token = await user.getIdToken();
    const res = await fetch('/api/users/me/lead-profile', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const data = await res.json();
    if (data.profile) {
      if (data.profile.applicantName) form.value.applicantName = data.profile.applicantName;
      if (data.profile.applicantEmail) form.value.applicantEmail = data.profile.applicantEmail;
      if (data.profile.applicantPhone) form.value.applicantPhone = data.profile.applicantPhone;
    }
  } catch { /* non-blocking */ }
}

function applySearchFilters() {
  const filters = props.searchFilters;
  if (!filters) return;
  if (filters.priceMax && !form.value.budgetMax) form.value.budgetMax = filters.priceMax;
}

function trackModalOpen() {
  if (props.mode === 'exit') return;
  const ciudad = props.city || props.coliving?.propertyLocation || props.coliving?.city || '';
  const colivingId = props.coliving?.id || '';
  const colivingNombre = props.coliving?.title || props.coliving?.propertyTitle || props.coliving?.externalSource || '';

  // GA4 + Meta — micro-conversión de alta intención (formulario visible)
  trackLeadModalOpen({
    modo: props.mode,
    colivingId,
    colivingNombre,
    ciudad,
    abVariant: variant,
  });

  // Backend analytics (dashboards internos)
  try {
    fetch('/api/analytics/lead-modal-open', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        variant,
        mode: props.mode,
        city: ciudad,
        colivingId: colivingId || null,
        timestamp: new Date().toISOString(),
      }),
    });
  } catch { /* fire and forget */ }
}

watch(
  () => props.show,
  (isOpen) => {
    if (isOpen) {
      submitted.value = false;
      errorMsg.value = '';
      codeCopied.value = false;
      promoNotifiedThisModal = false;
      showStayDetails.value = false;
      const sessionDefaults = profileToFormDefaults(getLeadSessionProfile());
      form.value = sessionDefaults
        ? { ...defaultForm(), ...sessionDefaults }
        : defaultForm();
      syncAuthState();
      tryPrefillProfile();
      applySearchFilters();
      trackModalOpen();
    }
  },
);

function handleClose() {
  if (isSubmitting.value) return;
  emit('close');
}

async function handleSubmit() {
  errorMsg.value = '';

  if (props.mode === 'exit') {
    if (!form.value.applicantEmail.trim()) {
      errorMsg.value = t('scrapedLeadModal.errorEmail');
      return;
    }

    isSubmitting.value = true;
    try {
      const result = await submitExitLead(form.value, {
        city: displayCity.value || '',
        searchFilters: props.searchFilters || null,
      });

      if (!result.ok) {
        const firstError = Array.isArray(result.data?.errors) ? result.data.errors[0]?.message : null;
        errorMsg.value = result.data?.error || firstError || t('scrapedLeadModal.errorGeneric');
        return;
      }

      submitted.value = true;
      // GA4 + Google Ads + Meta — conversión de exit-intent lead
      trackScrapedLeadSubmitted({
        ciudad: displayCity.value,
        modo: 'exit',
        abVariant: variant,
        leadId: result.leadId || '',
      });
      emit('submitted', result.leadId);
    } catch {
      errorMsg.value = t('scrapedLeadModal.errorConnection');
    } finally {
      isSubmitting.value = false;
    }
    return;
  }

  if (!form.value.applicantName.trim()) {
    errorMsg.value = t('scrapedLeadModal.errorName');
    return;
  }
  if (!form.value.applicantEmail.trim()) {
    errorMsg.value = t('scrapedLeadModal.errorEmail');
    return;
  }

  // Variant A requires dates/duration; Variant B makes them optional
  if (variant === 'A') {
    if (!form.value.stayDurationMonths) {
      errorMsg.value = t('scrapedLeadModal.errorDuration');
      return;
    }
    if (!form.value.desiredStartDate) {
      errorMsg.value = t('scrapedLeadModal.errorStartDate');
      return;
    }
  }

  const cityValue = props.city
    || props.coliving?.propertyLocation
    || props.coliving?.city
    || '';

  isSubmitting.value = true;
  try {
    let result;

    if (props.mode === 'listing' && props.coliving?.id) {
      result = await submitListingLead(
        props.coliving.id,
        form.value,
        {
          city: cityValue,
          searchFilters: props.searchFilters || null,
          abVariant: variant,
        },
      );
    } else {
      result = await submitSearchLead(form.value, {
        city: cityValue,
        searchFilters: props.searchFilters || null,
        abVariant: variant,
      });
    }

    if (!result.ok) {
      errorMsg.value = result.data?.error || t('scrapedLeadModal.errorGeneric');
      return;
    }

    submitted.value = true;
    // GA4 + Google Ads + Meta — MACRO-CONVERSIÓN principal del embudo
    trackScrapedLeadSubmitted({
      colivingId: props.coliving?.id || '',
      colivingNombre: props.coliving?.title || props.coliving?.propertyTitle || props.coliving?.externalSource || '',
      ciudad: cityValue,
      modo: props.mode === 'listing' && props.coliving?.id ? 'listing' : 'search',
      abVariant: variant,
      leadId: result.leadId || '',
    });
    emit('submitted', result.leadId);
  } catch {
    errorMsg.value = t('scrapedLeadModal.errorConnection');
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.scrollbar-hide {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

.scraped-lead-modal-sheet {
  max-height: 92dvh;
}

.scraped-lead-modal-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.scraped-lead-modal-footer {
  flex-shrink: 0;
  border-top: 1px solid rgb(243 244 246);
  background: #fff;
  padding: 0.625rem 1rem;
  padding-bottom: max(0.625rem, env(safe-area-inset-bottom));
  box-shadow: 0 -8px 24px rgba(15, 23, 42, 0.08);
}

@media (min-width: 640px) {
  .scraped-lead-modal-footer {
    box-shadow: none;
    padding: 0.75rem 1.5rem 1rem;
    padding-bottom: max(1rem, env(safe-area-inset-bottom));
  }
}

@supports not (height: 92dvh) {
  .scraped-lead-modal-sheet {
    max-height: 92vh;
  }
}
</style>
